import React, { useEffect, useState } from "react";
import { ChevronRight, ExpandLess, ExpandMore } from "@mui/icons-material";
import { Alert, Box, Button, Tooltip, Typography } from "@mui/material";

import { NavigationButton } from "ui";

import { BaseDataFlyoutTarget, selectBaseDataFlyoutExpanded, setBaseDataFlyoutExpanded } from "fond/redux/baseDataFlyout";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { ConfirmModal, useStackedNavigationContext } from "fond/widgets";

import AreaSelectSection from "./flyout/AreaSelectSection";
import BoundaryImport from "./flyout/BoundaryImport";
import BoundaryUpload from "./flyout/BoundaryUpload";
import FlyoutHeader from "./flyout/FlyoutHeader";
import LayerUpload from "./flyout/LayerUpload";
import { BaseDataOptionItem } from "./flyout/options";
import ReplaceConfirmation from "./flyout/ReplaceConfirmation";
import StatusCard from "./flyout/StatusCard";
import TaskProgress from "./flyout/TaskProgress";
import { BaseDataDrawParams } from "./BaseDataDraw";
import { BOUNDARY_SAVE_ERROR } from "./copy";
import { formatList } from "./gating";
import GenerationTask from "./GenerationTask";
import { BASE_DATA_LAYERS, BaseDataOption } from "./layers";
import type { BaseDataRowModel } from "./model";
import { BASE_DATA_POLYGON_DRAW_SCREEN, PolygonDrawParams } from "./polygonDraw";
import type { BaseDataModelActions } from "./useBaseDataModel";

const BASE_DATA_DRAW_SCREEN = "base-data-draw";

const OPTION_BUTTON_SX = { m: 0 };

interface BaseDataFlyoutProps {
  row: BaseDataRowModel;
  actions: BaseDataModelActions;
  /** Open another flyout, or close the flyout with null. */
  onSelect(target: BaseDataFlyoutTarget | null): void;
}

/** Map flyout for adding, generating and editing a single base data layer. */
const BaseDataFlyout: React.FC<BaseDataFlyoutProps> = ({ row, actions, onSelect }) => {
  const dispatch = useAppDispatch();
  const { open } = useStackedNavigationContext();
  const expanded = useAppSelector(selectBaseDataFlyoutExpanded);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const { key: layerKey, task: ownTask, hasData, options, next, generation } = row;
  const { name, noun, icon: Icon, info, layers } = BASE_DATA_LAYERS[layerKey];
  const isBoundary = layerKey === "boundary";
  const contents = formatList(layers.map((layer) => layer.name));
  const nextTarget = next.target;

  // A task's result line is shown until the option showing it closes.
  const setExpanded = (option: BaseDataOption | null) => {
    if (expanded !== null && ownTask?.status === "succeeded") actions.dismissTask(layerKey);
    dispatch(setBaseDataFlyoutExpanded(option));
  };
  const collapse = () => setExpanded(null);

  // A disabled option can't stay open, e.g. Import FOND base data once the boundary is deleted.
  const isExpandedOptionDisabled = expanded !== null && !!options.find(({ id }) => id === expanded)?.disabled;
  useEffect(() => {
    if (isExpandedOptionDisabled) dispatch(setBaseDataFlyoutExpanded(null));
  }, [dispatch, isExpandedOptionDisabled]);

  const handleOnDelete = () => {
    setIsConfirmingDelete(false);
    actions.deleteData(layerKey);
  };

  const generate = () => {
    collapse();
    if (generation) actions.run(generation.key);
  };

  const startDraw = () => {
    if (isBoundary) {
      const params: PolygonDrawParams = { purpose: "boundary" };
      open(BASE_DATA_POLYGON_DRAW_SCREEN, params);
    } else {
      const params: BaseDataDrawParams = { layerKey };
      open(BASE_DATA_DRAW_SCREEN, params);
    }
  };

  /** Why the row's delete or boundary save failed, which no option shows. */
  const renderRowTaskFailure = (): React.ReactNode => {
    if (ownTask?.status !== "failed" || (ownTask.kind !== "delete" && ownTask.kind !== "boundarySave")) return null;
    const dismiss = { label: "Cancel", onClick: () => actions.dismissTask(layerKey) };
    const error =
      ownTask.kind === "delete"
        ? {
            title: `Couldn't delete ${noun}`,
            reason: "There was a problem deleting your data. Please try again.",
            actions: [dismiss, ...(row.delete.enabled ? [{ label: "Retry", primary: true, onClick: () => actions.deleteData(layerKey) }] : [])],
          }
        : {
            title: "Couldn't save the boundary",
            reason: BOUNDARY_SAVE_ERROR,
            actions: [dismiss, ...(row.change.enabled ? [{ label: "Draw again", primary: true, onClick: startDraw }] : [])],
          };
    return <TaskProgress error={error} />;
  };

  /** Content shown under an expanded option. */
  const renderOptionContent = (option: BaseDataOption): React.ReactNode => {
    switch (option) {
      case "generate":
        return <ReplaceConfirmation layerNoun={noun} onCancel={collapse} onReplace={generate} />;
      case "importWithinBoundary":
        return <BoundaryImport />;
      case "importLayer":
        return <AreaSelectSection layerKey={layerKey} hasData={hasData} onCancel={collapse} />;
      case "upload":
        return isBoundary ? <BoundaryUpload hasBoundary={hasData} onCancel={collapse} /> : <LayerUpload layerKey={layerKey} />;
      default:
        return null;
    }
  };

  const handleOptionClick = (option: BaseDataOption, isExpanded: boolean) => {
    if (option === "draw") {
      startDraw();
    } else if (option === "generate" && !hasData) {
      generate();
    } else {
      setExpanded(isExpanded ? null : option);
    }
  };

  const renderOption = ({ id, icon: OptionIcon, title, subtitle, disabled }: BaseDataOptionItem) => {
    const isExpanded = expanded === id && !disabled;
    const navigates = id === "draw" || (id === "generate" && !hasData);
    const ExpandIcon = isExpanded ? ExpandLess : ExpandMore;
    return (
      <Box key={id} display="flex" flexDirection="column" gap={0.75}>
        <NavigationButton
          icon={<OptionIcon />}
          title={title}
          subtitle={subtitle}
          disabled={disabled}
          onClick={() => handleOptionClick(id, isExpanded)}
          rightElement={navigates ? <ChevronRight color="action" /> : <ExpandIcon color="action" />}
          sx={{ ...OPTION_BUTTON_SX, ...(isExpanded && { borderColor: "primary.light" }) }}
        />
        {isExpanded && renderOptionContent(id)}
        {/* Generation of this layer shows its progress and failures under the option. */}
        {id === "generate" && generation && (
          <GenerationTask generation={generation} onRetry={() => actions.run(generation.key)} onCancel={actions.cancel} />
        )}
      </Box>
    );
  };

  return (
    <Box display="flex" flexDirection="column" height="100%" bgcolor="background.paper" data-testid="base-data-flyout">
      <FlyoutHeader icon={Icon} title={name} onClose={() => onSelect(null)} />

      <Box flexGrow={1} minHeight={0} overflow="auto" display="flex" flexDirection="column" gap={2.5} p={1.75}>
        <Alert severity="info" variant="compact">
          {info}
        </Alert>

        <StatusCard layerKey={layerKey} lines={row.lines} hasData={hasData} deleteGate={row.delete} onDelete={() => setIsConfirmingDelete(true)} />
        {renderRowTaskFailure()}

        <Box display="flex" flexDirection="column" gap={1.25}>
          <Typography variant="body2" fontWeight={500}>
            {row.heading}
          </Typography>
          {options.map(renderOption)}
        </Box>
      </Box>

      <Box
        display="flex"
        alignItems="center"
        justifyContent="flex-end"
        gap={1}
        px={1.75}
        py={1.25}
        borderTop={1}
        borderColor="divider"
        flexShrink={0}
      >
        {nextTarget ? (
          // The guided walk-through waits for required layers; rows in the panel can still be opened in any order.
          <Tooltip title={next.blockedReason ?? `Go to ${nextTarget === "modifyData" ? "Modify data" : BASE_DATA_LAYERS[nextTarget].name}`}>
            <span>
              <Button
                variant="contained"
                size="small"
                endIcon={<ChevronRight />}
                disabled={!!next.blockedReason}
                onClick={() => onSelect(nextTarget)}
              >
                Next
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button variant="outlined" size="small" onClick={() => onSelect(null)}>
            Done
          </Button>
        )}
      </Box>

      {isConfirmingDelete && (
        <ConfirmModal
          open
          header={`Delete ${noun}?`}
          confirmText="Delete"
          color="error"
          content={
            <Typography variant="body2">
              {isBoundary ? "This removes the boundary from this version." : `This removes all ${contents} from this version.`}
            </Typography>
          }
          onConfirm={handleOnDelete}
          onCancel={() => setIsConfirmingDelete(false)}
          // Escape cancels; Enter deletes unless a button has focus, which handles Enter itself.
          onClose={(_event, reason) => reason === "escapeKeyDown" && setIsConfirmingDelete(false)}
          onKeyDown={(event: React.KeyboardEvent) => {
            if (event.key !== "Enter" || event.target instanceof HTMLButtonElement) return;
            // Stops the Enter reaching the Delete button that regains focus when the modal closes.
            event.preventDefault();
            handleOnDelete();
          }}
        />
      )}
    </Box>
  );
};

export default BaseDataFlyout;
